import { useMemo } from 'react';

import { deviceNumberSizes } from 'config/device';
import { useUserConfiguration } from 'config/themes';
import { SoftOneBoolean } from 'helpers/helpers';
import { useWindowSize } from 'hooks/useWindowResize';

import {
  createListData,
  createTableData,
  createDetailData,
  createTableColumns,
} from './Table.utils';
import Table from '../Table';
import {
  TablePageWrapper,
  MemoryTableWrapper,
  MobileTablePageWrapper,
} from '../Table.style';
import { EditableCell } from '../TableViews/EditableCell';

const Cell = EditableCell;

type TablePickerProps = {
  type: string;
  keyData: any;
  valueData: any;
  actions?: any;
  readOnly?: boolean;
  eopyyInfo?: any;
  tableOffset?: number;
  enableA11y?: boolean;
  handleUpdate?: any;
  handleClickRow?: any;
  handleEopyySuccess?: any;
  handleOneItem?: any;
  mobileType?: string;
  /** Hide New, Excel, column filter actions - use for pickers/embedded lists (ListWrapper, Search, Charts) */
  showNavActions?: boolean;
  /** Enable multi-row selection with a leading checkbox column (multi-import modal) */
  selectable?: boolean;
  /** Set of currently selected row IDs (only used when selectable) */
  selectedIds?: Record<string, boolean>;
  /** Toggle a single row's selection by row ID (only used when selectable) */
  onToggleRow?: (id: string) => void;
  /** Toggle all currently visible rows (select-all checkbox) */
  onToggleAll?: (ids: string[], checked: boolean) => void;
  /** Show per-row quantity input columns (multi-import modal) */
  qtyEnabled?: boolean;
  /** One descriptor per quantity column: { fieldId, label, width } */
  qtyColumns?: {
    fieldId: string;
    label: string;
    width?: number;
    fieldProperty?: string;
    numOfDecimals?: number;
  }[];
  /** Nested map of row ID -> (qty field id -> value) */
  qtyValues?: Record<string, Record<string, string>>;
  /** Change handler for a row's quantity input (per qty field) */
  onQtyChange?: (id: string, fieldId: string, value: string) => void;
};

const TablePicker = ({
  type,
  keyData,
  valueData,
  actions = undefined,
  readOnly = false,
  eopyyInfo,
  tableOffset,
  enableA11y = true,
  handleUpdate,
  handleClickRow,
  handleEopyySuccess,
  handleOneItem,
  mobileType,
  showNavActions = true,
  selectable = false,
  selectedIds,
  onToggleRow,
  onToggleAll,
  qtyEnabled = false,
  qtyColumns,
  qtyValues,
  onQtyChange,
}: TablePickerProps) => {
  const { showTableFilter, defaultPageSize } = useUserConfiguration();

  const windowSize = useWindowSize();
  const isTabletDown =
    (windowSize.width ? windowSize.width : 0) <= deviceNumberSizes.tablet;

  const tableColumns = useMemo(() => {
    if (!keyData) return [];
    if (type === 'List') {
      return createTableColumns(keyData, 'name', 'caption');
    }
    if (type === 'Detail') {
      return createTableColumns(keyData, 'FIELDCAPTION', null);
    }
    return createTableColumns(keyData, 'dataIndex', 'header');
  }, [keyData, type]);

  const tableData = useMemo(() => {
    if (!valueData) return [];
    if (type === 'List') {
      return createListData(valueData, keyData);
    }
    if (type === 'Detail') {
      return createDetailData(valueData, 'FIELDCAPTION', eopyyInfo);
    }
    return createTableData(valueData, keyData);
  }, [keyData, valueData, type, eopyyInfo]);

  if (type === 'List' && !(mobileType && isTabletDown)) {
    return (
      <MemoryTableWrapper $offset={tableOffset}>
        <Table
          usage={{ filter: true, enableA11y: true }}
          tableProps={{
            disableGroupBy: true,
            initialState: { hiddenColumns: ['FLD-1', 'ID'] },
          }}
          data={tableData}
          columns={tableColumns}
          provideCell={handleClickRow}
        />
      </MemoryTableWrapper>
    );
  }
  if (type === 'Detail') {
    // Type detail - eg. for item lines
    if (isTabletDown) {
      return (
        <MobileTablePageWrapper>
          <Table
            handleOneItem={handleOneItem ? handleOneItem : undefined}
            usage={{ enableA11y }}
            tableProps={{
              disableGroupBy: true,
              defaultColumn: { Cell },
              handleTableUpdate: handleUpdate,
              handleEopyySuccess,
              initialState: { hiddenColumns: ['ID'] },
            }}
            data={tableData}
            columns={tableColumns}
            provideCell={handleClickRow}
            actions={!readOnly && actions}
            type={type}
            isTabletDown={isTabletDown}
          />
        </MobileTablePageWrapper>
      );
    } else {
      return (
        <TablePageWrapper
          $isClickable={handleClickRow}
          $height={50}
          className='detailTableWrapper'
        >
          <Table
            handleOneItem={handleOneItem ? handleOneItem : undefined}
            usage={{ enableA11y }}
            tableProps={{
              disableGroupBy: true,
              defaultColumn: { Cell },
              handleTableUpdate: handleUpdate,
              handleEopyySuccess,
              initialState: { hiddenColumns: ['ID'] },
            }}
            data={tableData}
            columns={tableColumns}
            provideCell={handleClickRow}
            actions={!readOnly && actions}
          />
        </TablePageWrapper>
      );
    }
  }

  if (mobileType && isTabletDown) {
    return (
      <MobileTablePageWrapper className='mobileList'>
        <Table
          usage={{
            enableA11y,
            pagination: true,
            columnsFilter: showNavActions,
            // List: show filter. Search has TableSearchHeader - skip redundant filter.
            filter:
              type === 'List'
                ? true
                : type === 'Search'
                  ? false
                  : SoftOneBoolean(showTableFilter),
          }}
          tableProps={{
            initialState: {
              hiddenColumns: ['FLD-1', 'ID'],
              pageSize: Number(defaultPageSize ?? 10),
            },
          }}
          data={tableData}
          columns={tableColumns}
          provideCell={handleClickRow}
          {...{ actions }}
          type={type}
          mobileType={mobileType}
          isTabletDown={isTabletDown}
          selectable={selectable}
          selectedIds={selectedIds}
          onToggleRow={onToggleRow}
          onToggleAll={onToggleAll}
          qtyEnabled={qtyEnabled}
          qtyColumns={qtyColumns}
          qtyValues={qtyValues}
          onQtyChange={onQtyChange}
        />
      </MobileTablePageWrapper>
    );
  }

  return (
    <TablePageWrapper
      $isClickable={handleClickRow}
      $offset={tableOffset}
      className='defaultTableWrapper'
    >
      <Table
        usage={{
          enableA11y,
          pagination: true,
          columnsFilter: showNavActions,
          // Search has TableSearchHeader above - skip redundant filter input
          filter: type === 'Search' ? false : SoftOneBoolean(showTableFilter),
        }}
        tableProps={{
          initialState: {
            hiddenColumns: ['FLD-1', 'ID'],
            pageSize: Number(defaultPageSize ?? 10),
          },
        }}
        data={tableData}
        columns={tableColumns}
        provideCell={handleClickRow}
        {...{ actions }}
        selectable={selectable}
        selectedIds={selectedIds}
        onToggleRow={onToggleRow}
        onToggleAll={onToggleAll}
        qtyEnabled={qtyEnabled}
        qtyColumns={qtyColumns}
        qtyValues={qtyValues}
        onQtyChange={onQtyChange}
      />
    </TablePageWrapper>
  );
};

export default TablePicker;
